import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import AuthLogo from "../../Components/Auth/AuthLogo";
import AuthCard from "../../Components/Auth/AuthCard";
import AuthContainer from "../../Components/Auth/AuthContainer";
import PublicPageCard from "../../Components/Auth/PublicPageCard";
import { ErrorHandler } from "utils/toast";
import { getSecurePaymentDetails } from "slices/thunks";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { clearPaymentDetails } from "slices/payments/reducer";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import Icon from "Components/Icons/Icon";

const PaymentCheckout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parse payment ID and token from query parameters
  const queryPaymentId = searchParams.get("payment_id") || searchParams.get("id");
  const queryToken = searchParams.get("token") || "";
  const paymentIdFromUrl = queryPaymentId ? Number(queryPaymentId) : undefined;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  // Redux securePaymentDetails selector
  const securePaymentDetails = useAppSelector((state) => state.payment.securePaymentDetails);

  // Determine if it is a deposit payment or invoice payment
  const isDeposit = securePaymentDetails?.amount_type === "deposit";

  // Set page title dynamically
  useEffect(() => {
    document.title = isDeposit ? "EaziQuote - Pay Deposit" : "EaziQuote - Pay Invoice";
  }, [isDeposit]);

  // Fetch payment details if accessing directly via URL parameter
  useEffect(() => {
    if (paymentIdFromUrl) {
      const fetchDetails = async () => {
        try {
          setIsFetchingDetails(true);
          console.log("dispatching getSecurePaymentDetails with:", { id: paymentIdFromUrl, token: queryToken });
          const res = await dispatch(
            getSecurePaymentDetails({
              id: paymentIdFromUrl,
              token: queryToken,
            })
          ).unwrap();
          console.log("getSecurePaymentDetails success:", res);
        } catch (error) {
          console.error("getSecurePaymentDetails error:", error);
          ErrorHandler(error);
        } finally {
          setIsFetchingDetails(false);
        }
      };
      fetchDetails();
    }
    return () => {
      dispatch(clearPaymentDetails());
    };
  }, [paymentIdFromUrl, queryToken, dispatch]);

  // Determine redirect guard logic
  if (!paymentIdFromUrl || !queryToken) {
    return <Navigate to="/" replace />;
  }

  // Handle pay now action
  const handlePay = async () => {
    if (!paymentIdFromUrl) return;

    try {
      setIsLoading(true);
      if (securePaymentDetails?.stripe_payment_link_url) {
        window.location.href = securePaymentDetails.stripe_payment_link_url;
      }
      setIsLoading(false);
    } catch (error) {
      ErrorHandler(error);
      setIsLoading(false);
    }
  };

  // Loading state for fetching details
  if (isFetchingDetails) {
    return (
      <FullScreenLoader
        isLoading={isFetchingDetails}
        message="Loading..."
      />
    );
  }

  // Calculate rendering parameters based on details
  const displayAmount = securePaymentDetails ? Number(securePaymentDetails.amount) : 0;
  const paymentStatus = securePaymentDetails ? securePaymentDetails.status : "pending";

  const isAlreadyPaid = paymentStatus === "received" || paymentStatus === "paid";

  // Dynamic headings and descriptions
  const alreadyPaidTitle = isDeposit ? "Deposit Already Paid" : "Invoice Already Paid";
  const alreadyPaidDesc = isDeposit
    ? "This deposit has already been paid. No further action is required."
    : "This invoice has already been paid. No further action is required.";

  const checkoutTitle = isDeposit ? "Pay Deposit" : "Pay Invoice";
  const checkoutDesc = isDeposit
    ? "Thanks for accepting the quote. You can pay the deposit now or come back to it anytime using this link."
    : "Thank you for your business. Your invoice is ready for payment. You can pay it now or come back to it anytime using this link.";

  const checkoutButtonText = isDeposit ? `Pay Deposit £${displayAmount.toLocaleString("en-GB", { minimumFractionDigits: 0 })}` : `Pay Invoice £${displayAmount.toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;

  return (
    <React.Fragment>
      <ParticlesAuth>
        <AuthContainer>
          <Container>
            <Row>
              <Col lg={12}>
                <AuthLogo />
              </Col>
            </Row>

            <Row className="auth-container">
              <Col md={8} lg={6} xl={5}>
                <AuthCard>
                  {isAlreadyPaid ? (
                    <PublicPageCard
                      icon={<Icon name='SuccessTick' />}
                      title={alreadyPaidTitle}
                      description={alreadyPaidDesc}
                      buttonText="Got It"
                      onButtonClick={() => {
                        navigate(-1);
                      }}
                    />
                  ) : (
                    <PublicPageCard
                      title={checkoutTitle}
                      description={checkoutDesc}
                      buttonText={checkoutButtonText}
                      onButtonClick={handlePay}
                      isPoweredBy
                      isSkip
                      onSkip={() => {
                        navigate(-1);
                      }}
                      amount={displayAmount}
                      buttonLoading={isLoading}
                    />
                  )}
                </AuthCard>
              </Col>
            </Row>
          </Container>
        </AuthContainer>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default PaymentCheckout;
